/**
 * Real React application entrypoint — split out from `index.tsx` so the
 * runtime-config bootstrap (in `index.tsx`) can `await loadRuntimeConfig()`
 * before any module that reads the config (e.g. `constants/API/apiURL.ts`)
 * is evaluated.
 */

import 'styles/index.sass'
import 'styles/reset.css'
import 'styles/becka.css'
import './translations/i18n'
import './utils/polyfills'

import * as Sentry from '@sentry/react'
import * as serviceWorker from './serviceWorker'

import { CSSPlugin, gsap } from 'gsap'
import { AnalyticsEvent, initGoogleAnalytics, initHotjar, initIntercom, initMixpanel, initSentry, initUserpilot, logAnalyticsEvent } from 'utils/analytics'

import { App } from 'components/higher-order/App'
import Auth0Config from 'constants/auth/auth0'
import { Auth0Provider } from 'utils/auth'
import ErrorPage from 'components/pages/General/ErrorPage/ErrorPage'
import { createRoot } from 'react-dom/client'
import { installEmbedLoadErrorWatch, reportEmbedRenderCrash } from 'utils/analytics/embedLoadErrorWatch'
import { isEmbedRoute } from 'constants/router'

// Inside the Propstack partner iframe (embed routes) we suppress third-party support/tracking
// widgets for GDPR-processor posture and a cleaner UX — Backbone's own analytics stay on.
// Bootstrap runs before the router mounts, so the gate reads the live location. See ISSUE-2310.
const isEmbed = isEmbedRoute()

// Initialize google analytics (Backbone analytics — kept in embed)
initGoogleAnalytics()

// Initialize sentry (kept in embed)
initSentry()

// Stamp every Sentry event/breadcrumb with the embed decision so widget-suppression issues
// ("tracker wrongly loaded/suppressed in the embed") are filterable in production. See ISSUE-2310.
Sentry.setTag('isEmbed', isEmbed)

// Initialize mixpanel (Backbone product analytics — kept in embed, tagged with the crm/render_context
// origin dimensions of the tracking spec, and given iframe-safe persistence there). See ISSUE-3212.
initMixpanel(isEmbed)
if (isEmbed) installEmbedLoadErrorWatch()

// The decline wall is suppressed inside the Propstack iframe (ISSUE-2704), so this is the only
// trace that a broker is walking the flow under a declined consent. The route is re-read at event
// time, exactly like the wall's own gate in public/cookieWatcher.js.
window.addEventListener('CookiebotOnDecline', () => {
  if (!isEmbedRoute()) return
  Sentry.setTag('embedCookieDeclined', 'true')
  logAnalyticsEvent(AnalyticsEvent.EMBED_COOKIE_DECLINED, { pathname: window.location.pathname })
})

// Initialize Intercom (suppressed in embed)
if (!isEmbed) initIntercom()

// Initialize Hotjar (suppressed in embed)
if (!isEmbed) initHotjar()

// Initialize Userpilot (suppressed in embed)
if (!isEmbed) initUserpilot()

// Register gsap CSS plugin
gsap.registerPlugin(CSSPlugin)

const container = document.getElementById('root')
const root = createRoot(container!)
root.render(
  <Auth0Provider
    domain={Auth0Config.domain}
    clientId={Auth0Config.clientId}
    useRefreshTokens={true}
    useRefreshTokensFallback={true}
    cacheLocation={'localstorage'}
    authorizationParams={{
      audience: Auth0Config.audience,
      redirect_uri: window.location.origin,
      scope: 'openid profile email',
    }}
  >
    <Sentry.ErrorBoundary fallback={<ErrorPage />} onError={reportEmbedRenderCrash}>
      <App />
    </Sentry.ErrorBoundary>
  </Auth0Provider>
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
